import { useCallback, useEffect, useRef, useState } from 'react';
import { Phone, PhoneOff, PhoneCall, Clock } from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { relative } from '@/lib/format';
import {
  Badge, Button, Field, LoadingBlock, Modal, Select, Textarea, useToast,
} from '@/components/ui';
import type { CallListResponse, CallOutcome, CallRow } from '@shared/types';

/**
 * Placing a call, and writing down what came of it.
 *
 * ── Why the script is the biggest thing on screen ─────────────────────────
 *
 * Because it is what the operator is doing. The call itself happens on their
 * phone — Twilio rings them, then dials the other end — so this panel is not
 * a dialler with a script bolted on, it is a script with a dial button. The
 * notes box sits under it and is open before the call connects, because the
 * useful sentence is usually said in the first thirty seconds and typing it
 * afterwards from memory loses the half that mattered.
 *
 * ── Polling, and where it stops ───────────────────────────────────────────
 *
 * Twilio reports progress to a webhook, not to this page, so the only way to
 * show "ringing" is to ask. It asks every four seconds while a call is live
 * and stops the moment one ends — and stops regardless after a few minutes,
 * because a call whose final status never arrived is a reason to leave a tab
 * quiet rather than to keep a timer running all afternoon.
 */

/** Twilio's own words, in ours. Unknown states are shown as they arrive. */
const STATUS_WORDS: Record<string, string> = {
  queued: 'Calling your phone…',
  initiated: 'Calling your phone…',
  ringing: 'Your phone is ringing',
  'in-progress': 'Connected',
  completed: 'Call ended',
  busy: 'Busy',
  'no-answer': 'No answer',
  failed: 'Could not connect',
  canceled: 'Cancelled',
};

const OUTCOME_WORDS: Record<CallOutcome, string> = {
  spoke: 'Spoke to them',
  voicemail: 'Left a voicemail',
  no_answer: 'No answer',
  wrong_number: 'Wrong number',
  not_interested: 'Not interested',
  call_back: 'Call back later',
  demo_booked: 'Demo booked',
};

/** The two that mean "stop writing to them", so the UI can say so first. */
const OUTCOMES_THAT_STOP: CallOutcome[] = ['not_interested', 'wrong_number'];

const LIVE_STATUSES = ['queued', 'initiated', 'ringing', 'in-progress'];

export function CallPanel({ target, onClose, onChanged }: {
  target: { leadId?: string; orgId?: string; name: string; phone: string | null };
  onClose: () => void;
  onChanged?: () => void;
}) {
  const toast = useToast();
  const [data, setData] = useState<CallListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [dialling, setDialling] = useState(false);
  const [current, setCurrent] = useState<CallRow | null>(null);
  const [scriptId, setScriptId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [outcome, setOutcome] = useState<CallOutcome | ''>('');
  const [saving, setSaving] = useState(false);
  const pollsLeft = useRef(60);

  const query = target.leadId ? { lead_id: target.leadId } : { org_id: target.orgId ?? '' };

  const load = useCallback(async () => {
    try {
      const res = await api.get<CallListResponse>('/admin/calls', query);
      setData(res);
      setScriptId((id) => id || res.scripts[0]?.id || '');
      // The live call, if there is one, is whichever row we are watching.
      setCurrent((row) => (row ? res.calls.find((c) => c.id === row.id) ?? row : null));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load the call history');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.leadId, target.orgId]);

  useEffect(() => { void load(); }, [load]);

  const live = Boolean(current && LIVE_STATUSES.includes(current.status));

  useEffect(() => {
    if (!live) return;
    const timer = window.setInterval(() => {
      if (pollsLeft.current <= 0) return;
      pollsLeft.current -= 1;
      void load();
    }, 4000);
    return () => window.clearInterval(timer);
  }, [live, load]);

  async function dial() {
    setDialling(true);
    pollsLeft.current = 60;
    try {
      const res = await api.post<{ call: CallRow }>('/admin/calls', {
        lead_id: target.leadId ?? undefined,
        org_id: target.orgId ?? undefined,
      });
      setCurrent(res.call);
      toast.success('Answer your phone — it will dial ' + target.name + ' when you do');
      await load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'The call could not be placed');
    } finally {
      setDialling(false);
    }
  }

  async function save() {
    if (!current) return;
    setSaving(true);
    try {
      await api.patch(`/admin/calls/${current.id}`, {
        outcome: outcome || undefined,
        notes,
      });
      toast.success('Saved against ' + target.name);
      setNotes('');
      setOutcome('');
      await load();
      onChanged?.();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'That could not be saved');
    } finally {
      setSaving(false);
    }
  }

  const script = data?.scripts.find((s) => s.id === scriptId) ?? null;

  return (
    <Modal
      open
      onClose={onClose}
      title={`Call ${target.name}`}
      subtitle={target.phone ?? 'No phone number on this record'}
      size="lg"
      footer={
        <div className="flex flex-wrap items-center justify-between gap-3 w-full">
          <p className="text-xs text-faint">
            Your phone rings first. It dials {target.name} once you answer.
          </p>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Close</Button>
            <Button
              variant="primary"
              loading={dialling}
              disabled={!target.phone || live}
              icon={<Phone className="h-4 w-4" />}
              onClick={() => void dial()}
            >
              {live ? 'Call in progress' : 'Call'}
            </Button>
          </div>
        </div>
      }
    >
      {loading ? (
        <LoadingBlock />
      ) : (
        <div className="space-y-5">
          {current && (
            <div className="flex items-center gap-2.5 rounded-xl border border-line bg-raised/50 px-4 py-3">
              {live
                ? <PhoneCall className="h-4 w-4 text-brand shrink-0" />
                : <PhoneOff className="h-4 w-4 text-faint shrink-0" />}
              <div className="min-w-0 flex-1">
                <p className="text-sm text-ink">
                  {STATUS_WORDS[current.status] ?? current.status}
                  {current.duration_seconds ? ` · ${formatDuration(current.duration_seconds)}` : ''}
                </p>
                {current.error_message && (
                  <p className="text-xs text-danger mt-0.5">{current.error_message}</p>
                )}
              </div>
            </div>
          )}

          {(data?.scripts.length ?? 0) > 0 ? (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-3">
                <p className="label">Script</p>
                {(data?.scripts.length ?? 0) > 1 && (
                  <Select
                    value={scriptId}
                    onChange={(e) => setScriptId(e.target.value)}
                    className="h-8 text-xs w-56"
                  >
                    {data?.scripts.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </Select>
                )}
              </div>
              {/*
                Scrollable and monospaced, and deliberately plain. It is read
                while talking, so what matters is that the eye can find the
                line it left — not that it is pretty.
              */}
              <pre className="max-h-72 overflow-auto rounded-xl border border-line bg-raised/40 p-4 text-xs leading-relaxed whitespace-pre-wrap font-mono text-ink">
                {script?.body ?? ''}
              </pre>
            </div>
          ) : (
            <p className="text-sm text-muted">
              No call script yet. Write one under Templates with the audience set to
              “call”, or run <code className="text-xs">seedOutreachTemplates()</code> in
              the Apps Script editor to start from the default one.
            </p>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="What happened">
              <Select
                value={outcome}
                onChange={(e) => setOutcome(e.target.value as CallOutcome | '')}
              >
                <option value="">—</option>
                {(data?.outcomes ?? []).map((o) => (
                  <option key={o} value={o}>{OUTCOME_WORDS[o] ?? o}</option>
                ))}
              </Select>
            </Field>
            <div className="flex items-end">
              <Button
                variant="outline"
                disabled={!current || (!outcome && !notes.trim())}
                loading={saving}
                onClick={() => void save()}
              >
                Save against this call
              </Button>
            </div>
          </div>

          {outcome && OUTCOMES_THAT_STOP.includes(outcome) && (
            <p className="text-xs text-warn">
              Saving this also marks {target.name} as not to be contacted, so they will
              not be emailed or rung again.
            </p>
          )}

          <Field
            label="Notes"
            hint="What they told you — how many properties, what they use now, what annoyed them, when to ring back."
          >
            <Textarea
              rows={4}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Manages 14 rooms across 3 houses. Rent in a spreadsheet, receipts in WhatsApp. Renewal in March."
            />
          </Field>

          {(data?.calls.length ?? 0) > 0 && (
            <div className="space-y-2">
              <p className="label">Earlier calls</p>
              <ul className="space-y-2">
                {data?.calls.slice(0, 6).map((row) => (
                  <li key={row.id} className="flex items-start gap-2.5 text-sm">
                    <Clock className="h-3.5 w-3.5 text-faint shrink-0 mt-1" />
                    <div className="min-w-0 flex-1">
                      <p className="text-ink">
                        {row.outcome ? OUTCOME_WORDS[row.outcome] ?? row.outcome : row.status}
                        <span className="text-faint text-xs">
                          {' · '}{relative(row.created_at)}
                          {row.duration_seconds
                            ? ` · ${formatDuration(row.duration_seconds)}`
                            : ''}
                        </span>
                      </p>
                      {row.notes && <p className="text-xs text-muted mt-0.5">{row.notes}</p>}
                    </div>
                    {!row.outcome && <Badge tone="neutral">{row.status}</Badge>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}
